import React, { useCallback, useEffect, useState, useContext } from "react";
import { useTranslation, Trans } from "react-i18next";

import { validateKeylessLocation } from "./keylessService";
import { DomainContext } from "../../domain-repository/DomainRepository";
import { errorModalImgStyle, keylessColors } from "./utils";

import AlertModalManager from "../../components/alert-modal-wraper/alert-modal-manager";
import { KeylessButton } from "./components/KeylessButton";
import { KeylessModal } from "./components/KeylessModal";

export function KeylessValidateStation({
  handleNextStep,
  keylessToken,
  keylessSession,
}) {
  const { t } = useTranslation();

  const { localizationAssets } = useContext(DomainContext);

  const { error, requestLocation } = useGeolocation();

  const [isLoading, setIsLoading] = useState(false);
  const [openLeaveModal, setOpenLeaveModal] = useState(false);

  const handleConfirmStationClick = useCallback(() => {
    async function func() {
      const coords = await requestLocation();
      if (coords) {
        setIsLoading(true);
        validateKeylessLocation(keylessToken, {
          location: `${coords.latitude},${coords.longitude}`,
        })
          .then((res) => {
            handleNextStep(res.nextStep);
          })
          .catch((err) => {
            const messageKey = err.response.data;
            AlertModalManager.openModal(
              { title: t(messageKey), message: "" },
              () => {}
            );
          })
          .finally(() => setIsLoading(false));
      }
    }
    func();
  }, [keylessToken, requestLocation, handleNextStep, t]);

  const handleLeaveClick = useCallback(() => {
    window.location.href = "/";
  }, []);

  const [displayInitImage, setDisplayInitImage] = useState(false);

  if (displayInitImage) {
    return (
      <InitView
        videoSrc={localizationAssets["keylessInit"]}
        logoSrc={localizationAssets["keylessGuerinLogo"]}
        text={t("keylessInitMessage")}
      />
    );
  }

  return (
    <div style={styles.page}>
      <div className="container" style={styles.pageContent}>
        <img
          src={localizationAssets["keylessGuerinLogo"]}
          alt="Guerin"
          style={styles.logo}
        />
        <img src={localizationAssets['keylessStationImage']} alt='' style={styles.stationImage} />
        <p style={styles.title}>
          <Trans
            i18nKey="keylessStationValidate"
            values={{
              driverName: keylessSession.driverName,
              station: t(keylessSession.pickupStationName),
            }}
            components={[<br />]}
          />
        </p>
        <div className="grid gap-4">
          <KeylessButton
            onClick={handleConfirmStationClick}
            text={t("yes")}
            color="white"
            backgroundColor={keylessColors.orange}
            isLoading={isLoading}
          />
          <KeylessButton
            onClick={() => setOpenLeaveModal(true)}
            text={t("no")}
            color="white"
            backgroundColor="rgba(0, 0 , 0, .10)"
            disabled={isLoading}
          />
        </div>
      </div>
      <KeylessModal
        animation="slide-up"
        isOpen={openLeaveModal}
        img={localizationAssets['keylessError']}
        imgStyle={errorModalImgStyle}
        title={t("keylessWarning")}
        message={t("keylessNotInStationModal")}
        actions={<KeylessButton onClick={handleLeaveClick} text={t("leave")} />}
      />
      <KeylessModal
        isOpen={error !== null}
        img={localizationAssets['keylessError']}
        imgStyle={errorModalImgStyle}
        title={t("keylessWarning")}
        message={t("keylessLocationError")}
        actions={<KeylessButton onClick={handleLeaveClick} text={t("leave")} />}
      />
    </div>
  );
}

function InitView({ videoSrc, logoSrc, text }) {
  return (
    <div style={styles.initSection}>
      <video
        loop
        autoPlay
        muted
        src={videoSrc}
        style={styles.initVideo}
        alt=""
      />
      <img style={styles.initLogo} src={logoSrc} alt="" />
      <div className="fade-in" style={styles.initText}>{text}</div>
    </div>
  );
}

const styles = {
  initSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100dvh',
    padding: '10% 0 50%',
    background: 'rgba(0, 0, 0, 0.4)',
  },
  initVideo: { height: "100dvh", position: "absolute", top: "0", left: "0", zIndex: "-1" },
  initLogo: {
    width: "40%",
    maxWidth: "200px",
  },
  initText: {
    color: "white",
    fontSize: "2.5rem",
    textAlign: "center",
    fontFamily: "'BlissLight'",
    width: '60%'
  },
  page: {
    backgroundColor: keylessColors.green,
    height: "100dvh",
    paddingBottom: "60px",
    paddingTop: "30px",
    fontFamily: "'BlissLight'",
  },
  pageContent: {
    textAlign: "center",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "2rem",
  },
  logo: {
    height: "10vh",
    margin: "0 auto",
  },
  stationImage: {
    height: '20vh',
    margin: '0 auto',
  },
  title: {
    color: "white",
    fontSize: "2rem",
    flex: "1",
    margin: "0",
    lineHeight: "normal",
  },
};

const useGeolocation = () => {
  const [error, setError] = useState(null);

  const requestLocation = () => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        const errMsg = "Geolocation is not supported by your browser";
        setError(errMsg);
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          setError(null);
          resolve(coords);
        },
        (err) => {
          let errMsg;
          switch (err.code) {
            case err.PERMISSION_DENIED:
              errMsg = "User denied the request for Geolocation.";
              break;
            case err.POSITION_UNAVAILABLE:
              errMsg = "Location information is unavailable.";
              break;
            case err.TIMEOUT:
              errMsg = "The request to get user location timed out.";
              break;
            case err.UNKNOWN_ERROR:
            default:
              errMsg = "An unknown error occurred.";
              break;
          }
          setError(errMsg);
          resolve(null);
        }
      );
    });
  };

  return { error, requestLocation };
};
